import {
  Alert,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SendOutlined,
  StopOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import Search from 'antd/es/input/Search';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiCall } from '../../API/RestApi';
import { modalError } from '../../Component/Modal/ModalLayout';
import { WarningLetterAttachmentList } from './WarningLetterAttachments';

const { TextArea } = Input;
const { Text } = Typography;

const STATUS_COLORS = {
  draft: 'default',
  sent: 'blue',
  acknowledged: 'green',
  cancelled: 'red',
};

const formatDate = (value, withTime = false) => {
  if (!value) return '-';
  return moment(value).format(withTime ? 'DD-MM-YYYY HH:mm:ss' : 'DD-MM-YYYY');
};

function WarningLetterAdmin() {
  const token = useSelector((state) => state.user.token);
  const [form] = Form.useForm();
  const [cancelForm] = Form.useForm();
  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState();
  const [warningLevel, setWarningLevel] = useState();
  const [page, setPage] = useState({ current: 1, pageSize: 10, total: 0 });
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [alert, setAlert] = useState(null);
  const [attachmentFiles, setAttachmentFiles] = useState([]);

  const authHeaders = useMemo(() => ({ Authorization: 'Bearer ' + token }), [token]);

  const request = async (method, url, payload) => {
    const res = await apiCall({
      method,
      url,
      data: {
        data: payload || '',
        headers: authHeaders,
      },
    });
    if (!res || res.status !== 'success') {
      throw new Error(res?.message || 'Terjadi kesalahan saat memproses data');
    }
    return res;
  };

  const loadEmployees = async () => {
    try {
      const res = await request('GET', '/users?nopage=true');
      const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setEmployees(rows);
    } catch (e) {
      // Daftar surat tetap dapat dipakai walau endpoint employee tidak dapat diakses.
      setEmployees([]);
    }
  };

  const loadData = async (nextPage = page.current, nextPageSize = page.pageSize) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', nextPage);
      params.set('limit', nextPageSize);
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (warningLevel) params.set('warning_level', warningLevel);

      const res = await request('GET', `/warning-letters/admin?${params.toString()}`);
      const payload = res.data || {};
      setData(payload.data || []);
      setPage({
        current: payload.page || nextPage,
        pageSize: payload.limit || nextPageSize,
        total: payload.total_data || 0,
      });
    } catch (e) {
      modalError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData(1, page.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, warningLevel]);

  const employeeOptions = useMemo(() => employees.map((employee) => ({
    value: employee.users_id,
    label: `${employee.name || '-'}${employee.users_code ? ` (${employee.users_code})` : ''}`,
  })), [employees]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ warning_level: 'SP1', send_now: false });
    setAttachmentFiles([]);
    setFormOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setAttachmentFiles([]);
    form.setFieldsValue({
      letter_number: record.letter_number || '',
      users_id: record.users_id,
      warning_level: record.warning_level,
      subject: record.subject,
      violation_date: record.violation_date ? dayjs(record.violation_date) : null,
      violation: record.violation,
      corrective_action: record.corrective_action,
      effective_date: record.effective_date ? dayjs(record.effective_date) : null,
      valid_until: record.valid_until ? dayjs(record.valid_until) : null,
      notes: record.notes,
      send_now: false,
    });
    setFormOpen(true);
  };

  const toPayload = (values) => {
    const formData = new FormData();
    const fields = {
      letter_number: values.letter_number || '',
      users_id: values.users_id,
      warning_level: values.warning_level,
      subject: values.subject,
      violation_date: values.violation_date ? values.violation_date.format('YYYY-MM-DD') : '',
      violation: values.violation,
      corrective_action: values.corrective_action,
      effective_date: values.effective_date ? values.effective_date.format('YYYY-MM-DD') : '',
      valid_until: values.valid_until ? values.valid_until.format('YYYY-MM-DD') : '',
      notes: values.notes || '',
      send_now: Boolean(values.send_now),
    };

    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value === null || value === undefined ? '' : String(value));
    });

    attachmentFiles.forEach((file) => {
      const rawFile = file.originFileObj || file;
      if (rawFile) formData.append('attachments', rawFile);
    });

    return formData;
  };

  const submitForm = async (values) => {
    setLoading(true);
    try {
      const payload = toPayload(values);
      const res = editing
        ? await request('PUT', `/warning-letters/admin/${editing.warning_letter_id}`, payload)
        : await request('POST', '/warning-letters/admin', payload);
      setAlert(res.message);
      setFormOpen(false);
      setEditing(null);
      form.resetFields();
      setAttachmentFiles([]);
      await loadData();
    } catch (e) {
      modalError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const showDetail = async (record) => {
    setLoading(true);
    try {
      const res = await request('GET', `/warning-letters/admin/${record.warning_letter_id}`);
      setDetail(res.data);
      setDetailOpen(true);
    } catch (e) {
      modalError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const sendLetter = (record) => {
    Modal.confirm({
      title: 'Kirim surat teguran?',
      content: `Surat ${record.letter_number} akan dikirim ke ${record.employee_name || 'karyawan'}.`,
      okText: 'Kirim',
      cancelText: 'Batal',
      onOk: async () => {
        try {
          setLoading(true);
          const res = await request('POST', `/warning-letters/admin/${record.warning_letter_id}/send`);
          setAlert(res.message);
          await loadData();
        } catch (e) {
          modalError(e.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const openCancel = (record) => {
    setCancelTarget(record);
    cancelForm.resetFields();
    setCancelOpen(true);
  };

  const submitCancel = async (values) => {
    try {
      setLoading(true);
      const res = await request('POST', `/warning-letters/admin/${cancelTarget.warning_letter_id}/cancel`, {
        reason: values.reason,
      });
      setAlert(res.message);
      setCancelOpen(false);
      setCancelTarget(null);
      cancelForm.resetFields();
      await loadData();
    } catch (e) {
      modalError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteDraft = (record) => {
    Modal.confirm({
      title: 'Hapus draft?',
      content: `Draft ${record.letter_number} akan dihapus permanen.`,
      okText: 'Hapus',
      okButtonProps: { danger: true },
      cancelText: 'Batal',
      onOk: async () => {
        try {
          setLoading(true);
          const res = await request('DELETE', `/warning-letters/admin/${record.warning_letter_id}`);
          setAlert(res.message);
          await loadData();
        } catch (e) {
          modalError(e.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'No',
      width: 60,
      render: (_, __, index) => (page.current - 1) * page.pageSize + index + 1,
    },
    { title: 'No. Surat', dataIndex: 'letter_number', key: 'letter_number' },
    {
      title: 'Karyawan',
      key: 'employee',
      render: (_, record) => (
        <div>
          <div>{record.employee_name || '-'}</div>
          <Text type="secondary">{record.employee_code || '-'}</Text>
        </div>
      ),
    },
    { title: 'Level', dataIndex: 'warning_level', key: 'warning_level', width: 90 },
    { title: 'Perihal', dataIndex: 'subject', key: 'subject' },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 125,
      render: (value) => <Tag color={STATUS_COLORS[value] || 'default'}>{String(value || '-').toUpperCase()}</Tag>,
    },
    {
      title: 'Dibuat', dataIndex: 'created_at', key: 'created_at', width: 150,
      render: (value) => formatDate(value, true),
    },
    {
      title: 'Aksi', key: 'action', width: 230, fixed: 'right',
      render: (_, record) => (
        <Space className="warning-letter-actions">
          <Tooltip title="Detail"><Button icon={<EyeOutlined />} onClick={() => showDetail(record)} /></Tooltip>
          {record.status === 'draft' && (
            <>
              <Tooltip title="Edit"><Button type="primary" icon={<EditOutlined />} onClick={() => openEdit(record)} /></Tooltip>
              <Tooltip title="Kirim"><Button icon={<SendOutlined />} onClick={() => sendLetter(record)} /></Tooltip>
              <Tooltip title="Hapus"><Button danger icon={<DeleteOutlined />} onClick={() => deleteDraft(record)} /></Tooltip>
            </>
          )}
          {record.status !== 'draft' && record.status !== 'cancelled' && (
            <Tooltip title="Batalkan surat"><Button danger icon={<StopOutlined />} onClick={() => openCancel(record)} /></Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="warning-letter-page">
      <Helmet><title>Kelola Surat Teguran</title></Helmet>
      {alert && <Alert message={alert} type="success" showIcon closable style={{ marginBottom: 16 }} afterClose={() => setAlert(null)} />}

      <Row className="warning-letter-toolbar" justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 16 }}> 
        <Col><h3 style={{ margin: 0 }}>Kelola Surat Teguran</h3></Col>
        <Col xs={24} sm={8} md={6} lg={5}><Button className="warning-letter-create-btn" type="primary" icon={<PlusOutlined />} onClick={openCreate}>Buat Surat Teguran</Button></Col>
      </Row>

      <Row className="warning-letter-filters" gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={10}><Search placeholder="Cari no. surat, perihal, nama/kode karyawan" allowClear onSearch={setSearch} /></Col>
        <Col xs={12} md={5}>
          <Select placeholder="Semua status" allowClear style={{ width: '100%' }} onChange={setStatus}
            options={[
              { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Terkirim' },
              { value: 'acknowledged', label: 'Diterima' }, { value: 'cancelled', label: 'Dibatalkan' },
            ]} />
        </Col>
        <Col xs={12} md={5}>
          <Select placeholder="Semua level" allowClear style={{ width: '100%' }} onChange={setWarningLevel}
            options={['SP1', 'SP2', 'SP3'].map((value) => ({ value, label: value }))} />
        </Col>
      </Row>

      <Table
        className="responsive-data-table"
        size="small"
        rowKey="warning_letter_id"
        loading={loading}
        columns={columns}
        dataSource={data}
        scroll={{ x: 1100 }}
        pagination={page}
        onChange={(pagination) => loadData(pagination.current, pagination.pageSize)}
      />

      <Modal
        title={editing ? 'Edit Draft Surat Teguran' : 'Buat Surat Teguran'}
        open={formOpen}
        onCancel={() => { setFormOpen(false); setEditing(null); setAttachmentFiles([]); form.resetFields(); }}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={850}
        okText={editing ? 'Simpan Perubahan' : 'Simpan'}
      >
        <Form form={form} layout="vertical" onFinish={submitForm}>
          <Row gutter={16}>
            <Col xs={24} md={12}><Form.Item name="letter_number" label="Nomor Surat"><Input placeholder="Kosongkan untuk nomor otomatis" /></Form.Item></Col>
            <Col xs={24} md={12}>
              <Form.Item name="users_id" label="Karyawan" rules={[{ required: true, message: 'Karyawan wajib dipilih' }]}>
                <Select showSearch optionFilterProp="label" placeholder="Pilih karyawan" options={employeeOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="warning_level" label="Level Teguran" rules={[{ required: true, message: 'Level wajib diisi' }]}>
                <Select options={['WARNING','SP1', 'SP2', 'SP3'].map((value) => ({ value, label: value }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}><Form.Item name="subject" label="Perihal" rules={[{ required: true, message: 'Perihal wajib diisi' }]}><Input /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item name="violation_date" label="Tanggal Pelanggaran"><DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item name="effective_date" label="Tanggal Berlaku"><DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item name="valid_until" label="Berlaku Sampai"><DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" /></Form.Item></Col>
            <Col span={24}><Form.Item name="violation" label="Pelanggaran" rules={[{ required: true, message: 'Pelanggaran wajib diisi' }]}><TextArea rows={4} /></Form.Item></Col>
            <Col span={24}><Form.Item name="corrective_action" label="Tindakan Perbaikan" rules={[{ required: true, message: 'Tindakan perbaikan wajib diisi' }]}><TextArea rows={4} /></Form.Item></Col>
            <Col span={24}><Form.Item name="notes" label="Catatan"><TextArea rows={3} /></Form.Item></Col>

            <Col span={24}>
              <Form.Item label="Attachment">
                {editing?.attachments?.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>Attachment tersimpan</Text>
                    <WarningLetterAttachmentList attachments={editing.attachments} compact />
                    <Text type="secondary">Attachment baru akan ditambahkan tanpa menghapus attachment lama.</Text>
                  </div>
                )}
                <Upload
                  multiple
                  beforeUpload={() => false}
                  fileList={attachmentFiles}
                  onChange={({ fileList }) => setAttachmentFiles(fileList)}
                  onPreview={() => undefined}
                >
                  <Button icon={<UploadOutlined />}>Pilih Attachment</Button>
                </Upload>
                {attachmentFiles.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <WarningLetterAttachmentList attachments={attachmentFiles} compact />
                  </div>
                )}
                <Text type="secondary">File dikirim ke API menggunakan field multipart <code>attachments</code>.</Text>
              </Form.Item>
            </Col>
            {!editing && <Col span={24}><Form.Item name="send_now" label="Penyimpanan" initialValue={false}><Select options={[{ value: false, label: 'Simpan sebagai draft' }, { value: true, label: 'Simpan dan langsung kirim' }]} /></Form.Item></Col>}
          </Row>
        </Form>
      </Modal>

      <Modal title="Detail Surat Teguran" open={detailOpen} onCancel={() => setDetailOpen(false)} footer={<Button onClick={() => setDetailOpen(false)}>Tutup</Button>} width="min(1120px, calc(100vw - 24px))" styles={{ body: { maxHeight: '72vh', overflowY: 'auto', overflowX: 'auto', paddingBottom: 12 } }}>
        {detail && (
          <div style={{ minWidth: 1320, paddingBottom: 8 }}>
          <Descriptions bordered column={3} size="small" styles={{ content: { whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'normal', minWidth: 170 }, label: { whiteSpace: 'normal', minWidth: 110 } }}>
            <Descriptions.Item label="Nomor Surat">{detail.letter_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="Status"><Tag color={STATUS_COLORS[detail.status] || 'default'}>{String(detail.status || '-').toUpperCase()}</Tag></Descriptions.Item>
            <Descriptions.Item label="Karyawan">{detail.employee_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Kode Karyawan">{detail.employee_code || '-'}</Descriptions.Item>
            <Descriptions.Item label="Divisi">{detail.division_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Posisi">{detail.position_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Level">{detail.warning_level || '-'}</Descriptions.Item>
            <Descriptions.Item label="Tanggal Pelanggaran">{formatDate(detail.violation_date)}</Descriptions.Item>
            <Descriptions.Item label="Perihal" span={2}>{detail.subject || '-'}</Descriptions.Item>
            <Descriptions.Item label="Pelanggaran" span={2}>{detail.violation || '-'}</Descriptions.Item>
            <Descriptions.Item label="Tindakan Perbaikan" span={2}>{detail.corrective_action || '-'}</Descriptions.Item>
            <Descriptions.Item label="Tanggal Berlaku">{formatDate(detail.effective_date)}</Descriptions.Item>
            <Descriptions.Item label="Berlaku Sampai">{formatDate(detail.valid_until)}</Descriptions.Item>
            <Descriptions.Item label="Catatan" span={2}>{detail.notes || '-'}</Descriptions.Item>
            <Descriptions.Item label="Attachment" span={2}><WarningLetterAttachmentList attachments={detail.attachments || []} /></Descriptions.Item>
            <Descriptions.Item label="Dibuat Oleh">{detail.created_by_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Dibuat Pada">{formatDate(detail.created_at, true)}</Descriptions.Item>
            <Descriptions.Item label="Dikirim Oleh">{detail.sent_by_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Dikirim Pada">{formatDate(detail.sent_at, true)}</Descriptions.Item>
            <Descriptions.Item label="Dibaca Pada">{formatDate(detail.read_at, true)}</Descriptions.Item>
            <Descriptions.Item label="Diterima Pada">{formatDate(detail.acknowledged_at, true)}</Descriptions.Item>
            {detail.employee_response && <Descriptions.Item label="Tanggapan Karyawan" span={2}>{detail.employee_response}</Descriptions.Item>}
            {detail.status === 'cancelled' && <Descriptions.Item label="Alasan Pembatalan" span={2}>{detail.cancellation_reason || '-'}</Descriptions.Item>}
          </Descriptions>
          </div>
        )}
      </Modal>

      <Modal title="Batalkan Surat Teguran" open={cancelOpen} onCancel={() => { setCancelOpen(false); setCancelTarget(null); }} onOk={() => cancelForm.submit()} okButtonProps={{ danger: true }} okText="Batalkan Surat" confirmLoading={loading}>
        <Form form={cancelForm} layout="vertical" onFinish={submitCancel}>
          <Form.Item name="reason" label="Alasan Pembatalan" rules={[{ required: true, message: 'Alasan pembatalan wajib diisi' }]}><TextArea rows={4} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default WarningLetterAdmin;
